import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { ChatbotService } from '../../services/chatbot.service';
import { ChatMessage } from '../../models/chat-message.model';
import { GlobalService } from '../../services/global.service';
import { ProductsService } from '../../services/products.service';
import { GoogleAnalyticsService } from '../../services/google-analytics.service';

/**
 * Interface interna para os cards de produto no chatbot.
 */
interface ChatProduct {
  /** Nome do produto (ex: "Viagem") */
  name: string;
  /** Slug do produto para navegação */
  slug: string;
  /** Slug do sub-produto (se existir) */
  subSlug: string;
  /** URL da imagem do card */
  imageUrl: string;
  /** URL da imagem do botão de simulação */
  buttonUrl: string;
  /** ID do produto */
  idProduct: number;
  /** ID do sub-produto */
  idProductsTypes: number;
}

/**
 * Mapeamento estático de slugs para imagens do Ricardo.
 * Cada produto que tenha imagens customizadas no chatbot
 * deve ter a sua entrada aqui.
 */
const PRODUCT_IMAGE_MAP: Record<string, { image: string; button: string }> = {
  viagem: {
    image: 'assets/Ricardo/exemplo de viagem.png',
    button: 'assets/Ricardo/simular viagem.png',
  },
  pet: {
    image: 'assets/Ricardo/exemplo pet.png',
    button: 'assets/Ricardo/simular pet.png',
  },
};

/**
 * Componente do chatbot Ricardo — widget flutuante redesenhado
 * com apresentação dos produtos à venda conforme a homepage.
 *
 * appDevTag="shared/components/chatbot/chatbot"
 */
@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
})
export class ChatbotComponent implements OnInit, OnDestroy, AfterViewChecked {
  private destroy$ = new Subject<void>();

  @ViewChild('chatBody') chatBody!: ElementRef;
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLInputElement>;

  /** Controlo de visibilidade */
  isOpen = false;
  isMinimized = false;
  isLoading = false;
  isInitializing = false;

  /** Mensagens do chat */
  messages: ChatMessage[] = [];

  /** Texto actual no input */
  currentMessage = '';

  /** Produtos disponíveis para os cards do chatbot */
  chatProducts: ChatProduct[] = [];

  /**
   * Indica se existem mensagens enviadas pelo utilizador.
   * Usado para esconder os cards de produto após o utilizador começar a conversar.
   */
  get hasUserMessages(): boolean {
    return this.messages.some(m => m.sender === 'user');
  }

  /** Número de telefone do utilizador logado (formato: 351XXXXXXXXX) */
  private userNumber = 0;

  /** NIF do utilizador logado */
  private userNif = '';

  /** Flag para auto-scroll */
  private shouldScroll = false;

  /** Flag para evitar múltiplas cargas de produtos */
  private productsLoaded = false;

  constructor(
    private chatbotService: ChatbotService,
    private cdr: ChangeDetectorRef,
    public globalService: GlobalService,
    private productsService: ProductsService,
    private router: Router,
    private elementRef: ElementRef,
    private gaService: GoogleAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.syncUserData();
    console.log('🤖 [Chatbot] Iniciado com número:', this.userNumber, 'NIF:', this.userNif);
    this.loadChatProducts();
  }

  /**
   * Sincroniza os dados do utilizador autenticado (NIF e telemóvel)
   * a partir do customerClass do globalService.
   */
  private syncUserData(): void {
    const customer = this.globalService.loggedUserClass?.customerClass;
    if (!customer) {
      this.userNif = '';
      this.userNumber = 0;
      return;
    }

    // NIF do cliente autenticado
    this.userNif = customer.NIF || '';

    // Telemóvel: remover espaços/caracteres, garantir prefixo 351
    const rawContact = (customer.Contact || '').replace(/\s+/g, '').replace(/[^0-9]/g, '');
    if (rawContact.length === 9) {
      // Número português sem prefixo → adicionar 351
      this.userNumber = parseInt('351' + rawContact, 10);
    } else if (rawContact.startsWith('351') && rawContact.length === 12) {
      // Já tem prefixo
      this.userNumber = parseInt(rawContact, 10);
    } else if (rawContact.length > 0) {
      // Outro formato — usar tal como está
      this.userNumber = parseInt(rawContact, 10);
    } else {
      this.userNumber = 0;
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  /**
   * Carrega os produtos à venda para apresentar nos cards do chatbot.
   * Usa o mesmo endpoint da homepage (HomeGetProductsTypeAll) e
   * filtra os que têm ShowProductsHomeFeatured + IsForSale activos.
   */
  private loadChatProducts(): void {
    if (this.productsLoaded) return;

    this.productsService
      .HomeGetProductsTypeAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: products => {
          if (!products?.length) return;

          // Filtrar produtos activos para venda com ShowProductsHomeFeatured
          const activeProducts = products.filter(
            p => p.ShowProductsHomeFeatured && p.IsForSale && !p.DateEnd && !p.ProdutoInativo,
          );

          // Mapear para os cards do chatbot
          this.chatProducts = activeProducts
            .filter(p => {
              // Só incluir produtos que tenham imagens mapeadas no Ricardo
              const slug = (p.Slug || this.slugify(p.ShortName || p.Name || '')).toLowerCase();
              return !!this.findProductImages(slug);
            })
            .map(p => {
              const slug = (p.Slug || this.slugify(p.ShortName || p.Name || '')).toLowerCase();
              const images = this.findProductImages(slug)!;
              const parentSlug = (p as any)._parentSlug || p.Slug || this.slugify(p.ProductsName || p.Name || '');

              return {
                name: p.ShortName || p.Name || '',
                slug: parentSlug,
                subSlug: p.Slug || this.slugify(p.Name || ''),
                imageUrl: images.image,
                buttonUrl: images.button,
                idProduct: p.idProduct!,
                idProductsTypes: p.idProductsTypes!,
              };
            });

          this.productsLoaded = true;
          this.cdr.detectChanges();
          console.log('🤖 [Chatbot] Produtos carregados:', this.chatProducts.length);
        },
        error: err => {
          console.warn('🤖 [Chatbot] Erro ao carregar produtos:', err);
        },
      });
  }

  /**
   * Procura as imagens do Ricardo para um dado slug de produto.
   * Verifica correspondência parcial (ex: slug "seguro-viagem" contém "viagem").
   */
  private findProductImages(slug: string): { image: string; button: string } | null {
    // Correspondência directa
    if (PRODUCT_IMAGE_MAP[slug]) {
      return PRODUCT_IMAGE_MAP[slug];
    }

    // Correspondência parcial
    for (const key of Object.keys(PRODUCT_IMAGE_MAP)) {
      if (slug.includes(key) || key.includes(slug)) {
        return PRODUCT_IMAGE_MAP[key];
      }
    }

    return null;
  }

  /**
   * Navega para a página de simulação do produto.
   * Usa a mesma lógica da homepage (navToSimulation).
   */
  navigateToProduct(product: ChatProduct): void {
    console.log('🤖 [Chatbot] Navegar para produto:', product.name, product.slug, product.subSlug);
    this.gaService.trackEvent('chatbot_product_click', { product_name: product.name, product_slug: product.slug });

    // Fechar o chatbot antes de navegar
    this.close();

    // Navegar para a página de simulação
    if (product.subSlug) {
      this.router.navigate(['/seguros', product.slug, product.subSlug]);
    } else {
      this.router.navigate(['/seguros', product.slug], { queryParams: { simular: 'true' } });
    }
  }

  /**
   * Converte texto para slug URL-friendly
   */
  private slugify(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    if (this.isOpen && !this.isMinimized) {
      this.toggleMinimize();
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen || this.isMinimized) return;

    // Se foi um clique no próprio componente do chatbot
    if (this.elementRef.nativeElement.contains(event.target)) {
      return;
    }

    // Evita conflitos com o botão que aciona a abertura do chat (se os tiverem)
    const target = event.target as HTMLElement;
    if (target.closest && (target.closest('.floating-trigger') || target.closest('[chatbot-trigger]'))) {
      return;
    }

    // Minimizar por segurança para não perder a conversa se clicou onde não devia
    this.toggleMinimize();
  }

  /**
   * Abre o chatbot
   */
  open(): void {
    // Garantir dados actualizados do utilizador autenticado
    this.syncUserData();

    // Bloquear abertura se não houver dados de autenticação
    if (!this.userNif || !this.userNumber) {
      console.warn('🤖 [Chatbot] ⚠️ Não é possível abrir — utilizador sem NIF ou telemóvel.');
      return;
    }

    this.isOpen = true;
    this.isMinimized = false;
    this.globalService.isChatbotOpen = true;
    this.gaService.trackEvent('chatbot_open');
    this.cdr.detectChanges();

    // Registar o utilizador silenciosamente na primeira abertura
    if (this.messages.length === 0) {
      this.isInitializing = true;
      this.resetAndRegisterUser();
    }

    setTimeout(() => {
      this.focusInput();
    }, 350);
  }

  /**
   * Fecha o chatbot e termina a sessão (limpa a conversa)
   */
  close(): void {
    this.isOpen = false;
    this.globalService.isChatbotOpen = false;
    this.messages = [];
    this.currentMessage = '';
    this.cdr.detectChanges();
  }

  /**
   * Alterna entre minimizado e expandido
   */
  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
    if (!this.isMinimized) {
      setTimeout(() => {
        this.scrollToBottom();
        this.focusInput();
      }, 100);
    }
  }

  /**
   * Toggle de abertura/fecho (chamado pelo botão externo)
   */
  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Envia uma mensagem rápida das sugestões
   */
  sendQuickMessage(text: string): void {
    this.currentMessage = text;
    this.sendMessage();
  }

  /**
   * Envia a mensagem actual para o chatbot
   */
  sendMessage(): void {
    const text = this.currentMessage?.trim();
    if (!text || this.isLoading) return;

    // Comando "reset" — limpa conversa e re-regista utilizador
    if (text.toLowerCase() === 'reset') {
      this.currentMessage = '';
      this.messages = [];
      this.resetAndRegisterUser();
      this.cdr.detectChanges();
      console.log('🤖 [Chatbot] 🔄 Conversa resetada pelo utilizador');
      this.gaService.trackEvent('chatbot_reset');
      return;
    }

    this.gaService.trackEvent('chatbot_message_sent', { message_length: text.length });

    // Adicionar mensagem do utilizador
    const userMsg: ChatMessage = {
      text,
      sender: 'user',
      timestamp: new Date(),
      status: 'sending',
    };
    this.messages.push(userMsg);
    this.currentMessage = '';
    this.shouldScroll = true;
    this.isLoading = true;
    this.cdr.detectChanges();

    // Enviar para a API
    this.chatbotService
      .sendMessage(this.userNumber, text, this.userNif)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          userMsg.status = 'sent';

          // Adicionar resposta do bot
          const botMsg: ChatMessage = {
            text: response.message || 'Desculpe, não consegui processar o seu pedido.',
            sender: 'bot',
            timestamp: new Date(),
          };
          this.messages.push(botMsg);
          this.shouldScroll = true;
          this.isLoading = false;
          this.cdr.detectChanges();
          this.focusInput();

          // Registar consumo real de tokens no backend de forma não-bloqueante
          const promptTokens = Math.max(Math.ceil((text.length || 0) / 3.5) + 320, 20);
          const completionTokens = Math.max(Math.ceil((response.message?.length || 0) / 3.5), 10);
          this.chatbotService
            .logAITokenUsage({
              Module: 'Chatbot Ricardo',
              Action: 'chat_reply',
              AIModel: 'gpt-4o',
              PromptTokens: promptTokens,
              CompletionTokens: completionTokens,
              TotalTokens: promptTokens + completionTokens,
              ReferenceId: this.userNif ? `nif_${this.userNif}` : null,
              IdUser: this.globalService.loggedUserClass?.idUser || null,
              SessionId: `chat_${this.userNumber || 'anon'}_${Date.now()}`,
              Status: 'SUCCESS',
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe();
        },
        error: err => {
          console.error('\n🤖 [Chatbot] ❌ ══════ Erro ao enviar mensagem ══════');
          console.error('📊 Status: ' + err?.status + ' (' + err?.statusText + ')');
          console.error('📍 URL: ' + err?.url);
          console.error('📦 Body do erro: ' + JSON.stringify(err?.error));
          console.error('💬 Mensagem: ' + err?.message);
          console.error('🔗 Objecto completo:', err);
          console.error('🤖 [Chatbot] ══════ FIM ══════\n');

          userMsg.status = 'error';

          // Mensagem de erro contextualizada
          let errorText = 'Lamento, ocorreu um erro ao processar o seu pedido. Por favor, tente novamente.';
          if (err?.status === 0) {
            errorText = 'Erro de ligação — verifique se o domínio está autorizado (CORS).';
          } else if (err?.status === 401 || err?.status === 403) {
            errorText = 'Acesso não autorizado — verifique a API key.';
          } else if (err?.status === 404) {
            errorText = 'Endpoint não encontrado — verifique o URL da API.';
          } else if (err?.status >= 500) {
            errorText = 'Erro no servidor do chatbot. Tente novamente mais tarde.';
          }

          const errorMsg: ChatMessage = {
            text: errorText,
            sender: 'bot',
            timestamp: new Date(),
          };
          this.messages.push(errorMsg);
          this.shouldScroll = true;
          this.isLoading = false;
          this.cdr.detectChanges();
          this.focusInput();
        },
      });
  }

  /**
   * Regista o utilizador silenciosamente no servidor.
   * Corre em background, sem mostrar loading — o ecrã de boas-vindas
   * estático é apresentado de imediato.
   */
  private resetAndRegisterUser(): void {
    this.chatbotService
      .registerUser(this.userNumber, this.userNif)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          console.log('🤖 [Chatbot] ✅ Utilizador registado com sucesso:', response);
          // Atraso propositado para dar tempo à base de dados do bot de sincronizar
          // antes da primeira mensagem, evitando que a IA pergunte pelo NIF.
          setTimeout(() => {
            this.isInitializing = false;
            this.cdr.detectChanges();
          }, 2500);
        },
        error: err => {
          console.warn('🤖 [Chatbot] ⚠️ Registo do utilizador (pode já estar registado):', err?.status);
          setTimeout(() => {
            this.isInitializing = false;
            this.cdr.detectChanges();
          }, 2500);
        },
      });
  }

  /**
   * Formata a hora da mensagem
   */
  formatTime(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }

  /**
   * Formata o texto da mensagem (suporte básico a parágrafos)
   */
  formatMessage(text: string): string {
    if (!text) return '';
    return text.replace(/\n/g, '<br>');
  }

  /**
   * TrackBy para *ngFor das mensagens
   */
  trackByIndex(index: number): number {
    return index;
  }

  /**
   * TrackBy para *ngFor dos produtos
   */
  trackBySlug(_index: number, item: ChatProduct): string {
    return item.slug + '_' + item.idProductsTypes;
  }

  /**
   * Scroll automático para o fundo do chat
   */
  private scrollToBottom(): void {
    try {
      if (this.chatBody?.nativeElement) {
        this.chatBody.nativeElement.scrollTop = this.chatBody.nativeElement.scrollHeight;
      }
    } catch (e) {
      // Silenciar erro se o elemento ainda não existir
    }
  }

  /**
   * Coloca o foco no input de mensagem
   */
  private focusInput(): void {
    try {
      this.messageInput?.nativeElement?.focus();
    } catch (e) {
      // Silenciar
    }
  }

  ngOnDestroy(): void {
    // Garantir que a flag é limpa ao destruir o componente (ex: navegação)
    this.globalService.isChatbotOpen = false;

    if (!this.destroy$.closed) {
      this.destroy$.next();
      this.destroy$.complete();
    }
  }
}
